import React, { useState, useEffect } from 'react';

const CustomTimePicker = ({ 
  value, 
  onChange, 
  placeholder = "Select time...",
  label,
  required = false,
  className = "",
  theme = "tour-manager", // Default theme for tour manager
  use24Hour = false
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Theme configuration for different apps
  const themes = {
    "tour-manager": {
      primary: '#FF9500',
      primaryHover: '#FF8800',
      primaryBg: 'rgba(255,149,0,0.2)',
      border: '#FF9500'
    },
    workflows: {
      primary: '#FF3B30',
      primaryHover: '#FF3B30',
      primaryBg: 'rgba(255,59,48,0.2)',
      border: '#FF3B30'
    },
    bookings: {
      primary: '#FFFF00',
      primaryHover: '#FFFF33',
      primaryBg: 'rgba(255,255,0,0.2)',
      border: '#FFFF00'
    },
    catalog: {
      primary: '#17A2B8',
      primaryHover: '#138496',
      primaryBg: 'rgba(23,162,184,0.2)',
      border: '#17A2B8'
    }
  };

  const currentTheme = themes[theme] || themes["tour-manager"];

  // Parse initial time value
  const parseTime = (timeValue) => {
    if (!timeValue) return use24Hour ? { hours: '12', minutes: '00' } : { hours: '08', minutes: '00', period: 'AM' };
    
    if (timeValue.includes(':')) {
      // Handle HH:MM format
      const [hours, minutes] = timeValue.split(':');
      if (use24Hour) {
        return {
          hours: hours.padStart(2, '0'),
          minutes: minutes.padStart(2, '0')
        };
      } else {
        const hour24 = parseInt(hours);
        const period = hour24 >= 12 ? 'PM' : 'AM';
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        return {
          hours: hour12.toString().padStart(2, '0'),
          minutes: minutes.padStart(2, '0'),
          period
        };
      }
    }
    
    // Handle ISO string
    const date = new Date(timeValue);
    if (use24Hour) {
      return {
        hours: date.getHours().toString().padStart(2, '0'),
        minutes: date.getMinutes().toString().padStart(2, '0')
      };
    } else {
      const hours24 = date.getHours();
      const period = hours24 >= 12 ? 'PM' : 'AM';
      const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
      return {
        hours: hours12.toString().padStart(2, '0'),
        minutes: date.getMinutes().toString().padStart(2, '0'),
        period
      };
    }
  };

  const [selectedTime, setSelectedTime] = useState(() => parseTime(value));

  useEffect(() => {
    setSelectedTime(parseTime(value));
  }, [value, use24Hour]);

  const formatDisplayTime = () => {
    if (!selectedTime.hours && !selectedTime.minutes) return placeholder;
    
    if (use24Hour) {
      return `${selectedTime.hours}:${selectedTime.minutes}`;
    } else {
      return `${selectedTime.hours}:${selectedTime.minutes} ${selectedTime.period}`;
    }
  };

  const handleTimeChange = (type, newValue) => {
    const newTime = { ...selectedTime, [type]: newValue };
    setSelectedTime(newTime);
    
    // Convert to the format expected by the parent component
    let timeString;
    if (use24Hour) {
      timeString = `${newTime.hours}:${newTime.minutes}`;
    } else {
      // Convert to 24-hour format for consistency
      let hours24 = parseInt(newTime.hours);
      if (newTime.period === 'AM' && hours24 === 12) {
        hours24 = 0;
      } else if (newTime.period === 'PM' && hours24 !== 12) {
        hours24 += 12;
      }
      timeString = `${hours24.toString().padStart(2, '0')}:${newTime.minutes}`;
    }
    
    onChange(timeString);
  };

  const handleNow = () => {
    const now = new Date();
    const newTime = parseTime(now.toTimeString());
    setSelectedTime(newTime);
    
    let timeString;
    if (use24Hour) {
      timeString = `${newTime.hours}:${newTime.minutes}`;
    } else {
      let hours24 = parseInt(newTime.hours);
      if (newTime.period === 'AM' && hours24 === 12) {
        hours24 = 0;
      } else if (newTime.period === 'PM' && hours24 !== 12) {
        hours24 += 12;
      }
      timeString = `${hours24.toString().padStart(2, '0')}:${newTime.minutes}`;
    }
    
    onChange(timeString);
    setIsOpen(false);
  };

  const handleClear = () => {
    setSelectedTime(use24Hour ? { hours: '', minutes: '' } : { hours: '', minutes: '', period: 'AM' });
    onChange('');
    setIsOpen(false);
  };

  // Generate hour options
  const hourOptions = use24Hour 
    ? Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))
    : Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));

  // Generate minute options
  const minuteOptions = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-white text-[12px] mb-2" style={{ fontWeight: 300 }}>
          {label.includes('*') ? (
            <>
              {label.replace(' *', '')}
              <span className={`ml-1`} style={{ color: currentTheme.primary }}>*</span>
            </>
          ) : (
            <>
              {label}
              {required && <span className={`ml-1`} style={{ color: currentTheme.primary }}>*</span>}
            </>
          )}
        </label>
      )}
      
      {/* Input Field */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-3 text-white text-[12px] text-left outline-none hover:border-[rgba(255,255,255,0.2)] transition-colors flex items-center justify-between`}
        style={{ '--focus-border-color': currentTheme.border }}
        onFocus={(e) => e.target.style.borderColor = currentTheme.border}
        onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
      >
        <span className={(selectedTime.hours && selectedTime.minutes) ? 'text-white' : 'text-[rgba(255,255,255,0.5)]'}>
          {formatDisplayTime()}
        </span>
        <svg className="w-4 h-4 text-[rgba(255,255,255,0.5)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {/* Time Picker Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Time Picker */}
          <div className="absolute top-full left-0 right-0 mt-2 bg-black border border-[rgba(255,255,255,0.2)] rounded-lg p-4 z-50 shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white text-[12px] font-medium">Select Time</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-[rgba(255,255,255,0.5)] hover:text-white transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Time Selectors */}
            <div className="flex items-center justify-center gap-2 mb-4">
              {/* Hours */}
              <div className="flex flex-col items-center">
                <label className="text-[rgba(255,255,255,0.7)] text-[10px] mb-2">
                  {use24Hour ? 'Hour' : 'Hour'}
                </label>
                <div className="relative">
                  <select
                    value={selectedTime.hours}
                    onChange={(e) => handleTimeChange('hours', e.target.value)}
                    className="bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none appearance-none cursor-pointer pr-8"
                    style={{ 
                      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6,9 12,15 18,9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 8px center',
                      backgroundSize: '16px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = currentTheme.border}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
                  >
                    {hourOptions.map(hour => (
                      <option key={hour} value={hour} className="bg-black">
                        {hour}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <span className="text-white text-[16px] mt-6">:</span>

              {/* Minutes */}
              <div className="flex flex-col items-center">
                <label className="text-[rgba(255,255,255,0.7)] text-[10px] mb-2">Minutes</label>
                <div className="relative">
                  <select
                    value={selectedTime.minutes}
                    onChange={(e) => handleTimeChange('minutes', e.target.value)}
                    className="bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none appearance-none cursor-pointer pr-8"
                    style={{ 
                      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6,9 12,15 18,9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 8px center',
                      backgroundSize: '16px'
                    }}
                    onFocus={(e) => e.target.style.borderColor = currentTheme.border}
                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
                  >
                    {minuteOptions.map(minute => (
                      <option key={minute} value={minute} className="bg-black">
                        {minute}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* AM/PM for 12-hour format */}
              {!use24Hour && (
                <div className="flex flex-col items-center">
                  <label className="text-[rgba(255,255,255,0.7)] text-[10px] mb-2">Period</label>
                  <div className="relative">
                    <select
                      value={selectedTime.period}
                      onChange={(e) => handleTimeChange('period', e.target.value)}
                      className="bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none appearance-none cursor-pointer pr-8"
                      style={{ 
                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6,9 12,15 18,9'%3E%3C/polyline%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 8px center',
                        backgroundSize: '16px'
                      }}
                      onFocus={(e) => e.target.style.borderColor = currentTheme.border}
                      onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.2)'}
                    >
                      <option value="AM" className="bg-black">AM</option>
                      <option value="PM" className="bg-black">PM</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-between">
              <button
                onClick={handleClear}
                className="text-[11px] hover:opacity-70 transition-opacity"
                style={{ color: currentTheme.primary }}
              >
                Clear
              </button>
              <button
                onClick={handleNow}
                className="text-[11px] hover:opacity-70 transition-opacity"
                style={{ color: currentTheme.primary }}
              >
                Now
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomTimePicker;
